'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Flame, Zap, Ship, Telescope, TrendingUp } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function TroisBatonsPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Bâtons</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Trois</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/batons"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Bâtons'a Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 rounded-xl p-8 mb-8 border border-red-600/30">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl bg-gradient-to-b from-red-100 to-orange-50 border border-red-300 flex flex-col items-center justify-center relative">
                {/* Card Visual Design */}
                <div className="text-center">
                  <div className="flex justify-center gap-2 mb-4">
                    <div className="text-2xl">🏴</div>
                    <div className="text-2xl">🏴</div>
                    <div className="text-2xl">🏴</div>
                  </div>
                  <div className="w-16 h-1 bg-red-600 mx-auto mb-4"></div>
                  <div className="text-2xl font-bold text-red-800">III</div>
                  <div className="text-sm text-red-700 mt-2">de Bâtons</div>
                </div>
                {/* Decorative elements */}
                <div className="absolute top-4 left-4 text-red-600">
                  <Ship className="h-4 w-4" />
                </div>
                <div className="absolute top-4 right-4 text-red-600">
                  <Telescope className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 left-4 text-red-600">
                  <TrendingUp className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 right-4 text-red-600">
                  <Ship className="h-4 w-4" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">III</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Trois de Bâtons</h1>
                  <p className="text-xl text-red-400">Genişleme / Öngörü</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde öngörü ve genişlemenin kartı. Uzaklara bakan figür,
                gelecekteki fırsatları görme yetisini simgeler. Ticaret, seyahat ve uluslararası
                bağlantıların habercisidir.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Öngörü</span>
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Genişleme</span>
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Ticaret</span>
                <span className="px-3 py-1 bg-red-600/20 text-red-300 rounded-full text-sm">Seyahat</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Flame className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Öngörü ve Planlama</h3>
                  <p className="text-slate-400 text-sm">Gelecekteki fırsatları görme, uzun vadeli stratejiler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İş Genişlemesi</h3>
                  <p className="text-slate-400 text-sm">Yeni pazarlar, uluslararası ticaret ve büyüme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Seyahat ve Keşif</h3>
                  <p className="text-slate-400 text-sm">Yeni yerler, kültürler ve deneyimler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Liderlik</h3>
                  <p className="text-slate-400 text-sm">Başkalarını yönlendirme, örnek olma.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Öngörü Eksikliği</h3>
                  <p className="text-slate-400 text-sm">Geleceği görememe, kısa vadeli düşünce.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Engellenen Planlar</h3>
                  <p className="text-slate-400 text-sm">Gecikmeler, iptaller ve beklenmedik sorunlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İşbirliği Sorunları</h3>
                  <p className="text-slate-400 text-sm">Ortaklarla anlaşmazlık, güven sorunları.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yalnızlık</h3>
                  <p className="text-slate-400 text-sm">İzolasyon, destek eksikliği, tek başına kalma.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Ship className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Gemi/Ticaret</h3>
              <p className="text-slate-400 text-sm">Uzak diyarlara ticaret, genişleme ve keşif.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Telescope className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Uzaklara Bakış</h3>
              <p className="text-slate-400 text-sm">Öngörü, vizyon ve geleceği görme yetisi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Üç Değnek</h3>
              <p className="text-slate-400 text-sm">Yaratıcılık, büyüme ve manifestasyon.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Uluslararası ticaret</li>
                <li>• İş genişletme projeleri</li>
                <li>• Yeni pazarlara açılma</li>
                <li>• Stratejik ortaklıklar</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Flame className="h-5 w-5 text-red-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ufuk genişletme</li>
                <li>• Seyahat ve keşif</li>
                <li>• Yeni kültürlerle tanışma</li>
                <li>• Liderlik gelişimi</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/batons/deux"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Deux de Bâtons
          </Link>

          <Link
            href="/about/tarot/systems/marseille/minor-arcana/batons/quatre"
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Quatre de Bâtons
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}